import React from 'react';
import useThemeMap from '../hooks/useThemeMap';
import { THEME_KINDS } from '../lib/inversion-results';
import { useTheme } from '../contexts/ThemeContext';

/**
 * ThemeMapPanel — layer-property maps from an inversion model (Wave 3.2):
 * top-of-layer depth where a property crosses a threshold, thickness between two
 * thresholds, mean/min/max between two depths, depth to the strongest interface.
 * Each run adds one map layer (one point per model column) with the cool-to-warm ramp;
 * the map's ColorScaleLegend shows its range.
 *
 * Props:
 *   entry      - model entry { id, name, model, cells, backendId? }
 *   onAddLayer - (geojson, name, kind) => void
 *   onClose()
 */
export default function ThemeMapPanel({ entry, onAddLayer, onClose }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC };
  const t = useThemeMap({ entry, onAddLayer });
  const { form, setField } = t;
  const columns = entry?.model?.columns || [];
  const props = ['resistivity', 'chargeability', 'sensitivity', 'misfit'].filter((p) => columns.includes(p));

  const selStyle = { width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' };
  const lbl = { fontSize: 10, color: C.text2, display: 'block', marginBottom: 2 };
  const num = (key, label, step = 'any') => (
    <div style={{ flex: 1 }}>
      <label style={lbl} htmlFor={`theme-${key}`}>{label}</label>
      <input id={`theme-${key}`} type="number" step={step} value={form[key] ?? ''} onChange={(e) => setField({ [key]: e.target.value })} style={selStyle} />
    </div>
  );

  return (
    <div role="dialog" aria-label="Theme maps" style={{ position: 'fixed', right: 290, top: 90, width: 290, zIndex: 40, background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.45)' }}>
      <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>🗺 Theme Maps — {entry?.name}</span>
        <button onClick={onClose} aria-label="Close theme maps" style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div>
          <label style={lbl} htmlFor="theme-kind">Map</label>
          <select id="theme-kind" value={t.kind} onChange={(e) => t.setKind(e.target.value)} style={selStyle}>
            {THEME_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
        </div>
        {(t.kind === 'top_depth' || t.kind === 'thickness' || t.kind === 'layer_stat') && props.length > 1 && (
          <div>
            <label style={lbl} htmlFor="theme-property">Property</label>
            <select id="theme-property" value={form.property || 'resistivity'} onChange={(e) => setField({ property: e.target.value })} style={selStyle}>
              {props.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}

        {t.kind === 'top_depth' && (
          <div style={{ display: 'flex', gap: 6 }}>
            {num('threshold', 'Threshold')}
            <div style={{ flex: 1 }}>
              <label style={lbl} htmlFor="theme-direction">First depth where</label>
              <select id="theme-direction" value={form.direction} onChange={(e) => setField({ direction: e.target.value })} style={selStyle}>
                <option value="above">value ≥ threshold</option>
                <option value="below">value ≤ threshold</option>
              </select>
            </div>
          </div>
        )}
        {t.kind === 'thickness' && (
          <div style={{ display: 'flex', gap: 6 }}>
            {num('min_value', 'From value')}
            {num('max_value', 'To value')}
          </div>
        )}
        {t.kind === 'layer_stat' && (
          <>
            <div style={{ display: 'flex', gap: 6 }}>
              {num('depth_min', 'Top depth (m)')}
              {num('depth_max', 'Bottom depth (m)')}
            </div>
            <div>
              <label style={lbl} htmlFor="theme-stat">Statistic</label>
              <select id="theme-stat" value={form.stat} onChange={(e) => setField({ stat: e.target.value })} style={selStyle}>
                <option value="mean">mean (geometric for resistivity)</option>
                <option value="min">minimum</option>
                <option value="max">maximum</option>
              </select>
            </div>
          </>
        )}
        {t.kind === 'interface_depth' && (
          <div style={{ display: 'flex', gap: 6 }}>
            {num('depth_min', 'Search from (m)')}
            {num('depth_max', 'to (m)')}
          </div>
        )}

        <button onClick={t.run} disabled={t.busy}
          style={{ fontSize: 11, padding: '7px', borderRadius: 8, background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', border: 'none', fontWeight: 600, cursor: t.busy ? 'wait' : 'pointer' }}>
          {t.busy ? 'Computing…' : 'Add map layer'}
        </button>
        {t.error && <div role="alert" style={{ fontSize: 10, color: C.red, padding: '5px 8px', background: 'rgba(248,113,113,0.1)', borderRadius: 6 }}>✗ {t.error}</div>}
        {t.last && (
          <div style={{ fontSize: 10, color: C.emerald, fontFamily: 'monospace', lineHeight: 1.6 }}>
            ✓ {t.last.n} points · {Number(t.last.min).toPrecision(4)} – {Number(t.last.max).toPrecision(4)}
          </div>
        )}
      </div>
    </div>
  );
}
